import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";

import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import FlightService from "@/modules/Flight/services/flight.service";
import AirportService from "@/core/services/airport.service";
import OrganizationService from "@/modules/Organization/services/organization.service";
import { FLIGHTS_ANALYTICS_COMMON_EVENTS } from "@/core/constants/analyticsEvents";

import {
  ACTIVE_PROVIDERS_AND_AIRLINES_API_RESPONSE,
  FetchFlightsPayload,
  FlightPreBookingTokenType,
  FlightResponse,
  Place,
} from "@/core/types/flight";
import {
  AirportLocation,
  FlightSearchSetupForm,
  IFlightSearch,
} from "@/core/interfaces/IFlightSearch.interface";
import type { IPassenger } from "@/core/interfaces/passenger.interface";
import analyticsService from "@/analytic.service";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { StoreState } from "@/store/type";
import { ROUTE_TYPE } from "@/core/enums/ROUTE_TYPE";
import { parseIntoCurrentDateIgnoringTimezone } from "@/core/utils/helpers";

const actions: ActionTree<IFlightSearch, StoreState> = {
  // **** Fetch Airports ****
  async fetchAirports(
    context: ActionContext<IFlightSearch, StoreState>,
    payload: { location: string; type: "origin" | "destination" },
  ) {
    try {
      if (payload.type === "origin") {
        context.commit("loadingOriginAirports", true);
      } else if (payload.type === "destination") {
        context.commit("loadingDestinationAirports", true);
      }

      const fetchAirports = new AirportService();
      const response: ICoreResponse<any> = await fetchAirports.get(
        payload.location,
      );
      if (response.success && response.status === StatusCodes.OK) {
        const formattedAirports = response.data?.data?.map((item: Place) => {
          return {
            ...item,
            label: `${item.city} (${item.iata_code})`,
          };
        });

        if (payload.location === "Pakistan") {
          context.commit("saveAirports", formattedAirports);
          context.commit("saveOriginAirports", formattedAirports);
          context.commit("saveDestinationAirports", formattedAirports);
        }

        if (payload.type === "origin") {
          context.commit("saveOriginAirports", formattedAirports);
        } else if (payload.type === "destination") {
          context.commit("saveDestinationAirports", formattedAirports);
        }
      } else {
        throw response;
      }
    } catch (err) {
      console.error("Error in fetchAirports:", err);
    } finally {
      if (payload.type === "origin") {
        context.commit("loadingOriginAirports", false);
      } else if (payload.type === "destination") {
        context.commit("loadingDestinationAirports", false);
      }
    }
  },

  async fetchFlightSearchSetupForm(
    context: ActionContext<IFlightSearch, StoreState>,
    payload?: { organizationId?: string },
  ) {
    try {
      context.commit("loadingOriginAirports", true);
      context.commit("loadingDestinationAirports", true);
      context.commit("loadingFinancialProfiles", true);

      const fetchAirports = new AirportService();
      const response: ICoreResponse<any> =
        await fetchAirports.getFlightSearchSetupDetails(
          payload?.organizationId,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const setupResponse = response.data as FlightSearchSetupForm;
        context.commit("saveSearchFinancialProfiles", null);
        const searchFinancialProfiles = setupResponse?.financial_profiles;
        const enabledAirlines = setupResponse?.enabled_airlines;
        const formattedOriginAirports = setupResponse?.origin_locations?.map(
          (item: AirportLocation) => {
            return {
              ...item,
              label: `${item.city} (${item.iata_code})`,
            };
          },
        );
        const currencyCodes = setupResponse?.currency_codes || [];

        const formattedDestinationAirports =
          setupResponse?.destination_locations?.map((item: AirportLocation) => {
            return {
              ...item,
              label: `${item.city} (${item.iata_code})`,
            };
          });

        const allAirports = [
          ...(formattedOriginAirports || []),
          ...(formattedDestinationAirports || []),
        ];
        context.commit("saveAirports", allAirports);
        context.commit("saveOriginAirports", formattedOriginAirports);
        context.commit("saveDestinationAirports", formattedDestinationAirports);
        context.commit("saveSearchFinancialProfiles", searchFinancialProfiles);
        context.commit("saveEnabledAirlines", enabledAirlines);
        context.commit("saveCurrencyCodes", currencyCodes);
      }
    } catch (err) {
      console.error("Error in fetchAirports:", err);
    } finally {
      context.commit("loadingOriginAirports", false);
      context.commit("loadingDestinationAirports", false);
      context.commit("loadingFinancialProfiles", false);
    }
  },

  async fetchMultiCityAirports(
    context: ActionContext<IFlightSearch, StoreState>,
    payload: {
      location: string;
      type: "origin" | "destination";
      index: number;
    },
  ) {
    try {
      if (payload.index !== null) {
        context.commit("setMulticityAirportInputLoading", {
          type: payload.type,
          index: payload.index,
        });
      }

      const fetchAirports = new AirportService();
      const response: ICoreResponse<any> = await fetchAirports.get(
        payload.location,
      );
      if (response.success && response.status === StatusCodes.OK) {
        const formattedAirports = response.data?.data?.map((item: Place) => {
          return {
            ...item,
            label: `${item.city} (${item.iata_code})`,
          };
        });

        if (payload.location === "Pakistan") {
          context.commit("saveAirports", formattedAirports);
          context.commit("setMulticityAirports", {
            airports: formattedAirports,
            type: "origin",
            index: 0,
          });
          context.commit("setMulticityAirports", {
            airports: formattedAirports,
            type: "destination",
            index: 0,
          });
          context.commit("setMulticityAirports", {
            airports: formattedAirports,
            type: "origin",
            index: 1,
          });
          context.commit("setMulticityAirports", {
            airports: formattedAirports,
            type: "destination",
            index: 1,
          });
        }

        if (payload?.index?.toString()) {
          context.commit("setMulticityAirports", {
            airports: formattedAirports,
            type: payload.type,
            index: payload.index,
          });
        }
      } else {
        throw response;
      }
    } catch (err) {
      console.error("Error in fetchMultiCityAirports:", err);
    } finally {
      context.commit("setMulticityAirportInputLoading", {
        type: null,
        index: null,
      });
    }
  },

  // **** Fetch Flights ****
  async fetchFlights(
    context: ActionContext<IFlightSearch, StoreState>,
    payload: FetchFlightsPayload,
  ) {
    try {
      if (!payload.poll_id) {
        context.commit("loadingFlights", true);
      }

      const flightService = new FlightService();
      const response: ICoreResponse = await flightService.post(payload);

      if (response.success && response.status === StatusCodes.OK) {
        const flightResponse: FlightResponse = response.data.data;

        context.commit("setFlightsResponse", flightResponse);

        if (flightResponse.journey_legs.length > 0) {
          context.commit("saveFlights", flightResponse);
          context.commit("saveCabinClass", payload.cabin_class);
          context.commit("saveRouteType", payload.route_type);

          if (payload.route_type === ROUTE_TYPE.MULTI_CITY) {
            const journey_legs = flightResponse.journey_legs;
            const multicityAirports = journey_legs.map((leg) => {
              return {
                origin: [leg.origin],
                destination: [leg.destination],
              };
            });

            context.commit("saveBulkMulticityAirports", multicityAirports);

            const multicityLegs = journey_legs.map((leg) => {
              return {
                departure_date: parseIntoCurrentDateIgnoringTimezone(
                  leg.departure_date,
                ),
                origin: leg.origin,
                destination: leg.destination,
              };
            });

            context.commit("saveMulticityLegs", multicityLegs);
          } else {
            const origin = flightResponse.journey_legs[0]?.origin || {};
            const destination =
              flightResponse.journey_legs[0]?.destination || {};
            const departure_date =
              flightResponse.journey_legs[0]?.departure_date;
            const return_date = flightResponse.journey_legs[1]?.departure_date;

            context.commit("saveOriginAirports", [origin]);
            context.commit("saveDestinationAirports", [destination]);
            context.commit("saveOrigin", origin);
            context.commit("saveDestination", destination);
            context.commit(
              "saveDepartureDate",
              parseIntoCurrentDateIgnoringTimezone(departure_date),
            );
            if (return_date) {
              context.commit(
                "saveReturnDate",
                parseIntoCurrentDateIgnoringTimezone(return_date),
              );
            }
          }

          context.commit("saveRouteType", payload.route_type);
          context.commit("saveTravelersCount", payload.traveler_count);
        }

        const analyticsPayload = {
          from: payload.origin,
          to: payload.destination,
          "departure-date": payload.departure_date,
          "return-date": payload.return_date,
          "route-type": payload.route_type,
          "adult-traveler-count": payload.traveler_count.adult_count,
          "child-traveler-count": payload.traveler_count.child_count,
          "infant-traveler-count": payload.traveler_count.infant_count,
          "cabin-class": payload.cabin_class,
          "non-stop-flight": false,
        };

        analyticsService.logActionEvent(
          FLIGHTS_ANALYTICS_COMMON_EVENTS.BOOKING_SEARCH_APPLY,
          analyticsPayload,
        );

        context.commit("setIsPolling", flightResponse.keep_polling);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      context.commit("setIsPolling", false);
    } finally {
      context.commit("loadingFlights", false);
    }
  },

  // INITIATE BOOKING
  async initiateBooking(
    context: ActionContext<IFlightSearch, StoreState>,
    {
      payload,
      successCallback,
    }: {
      payload: FlightPreBookingTokenType;
      successCallback: (booking_id: string) => void;
    },
  ) {
    try {
      context.commit("setLoading", true);
      const flightService = new FlightService();
      const response: ICoreResponse =
        await flightService.initiateBooking(payload);
      if (response.success && response.status === StatusCodes.OK) {
        const booking_id = response?.data?.data?.booking_id;
        successCallback(booking_id);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setLoading", false);
    }
  },

  // FETCH AIRLINES AND SABRE PROVIDERS
  async fetchAirlinesAndProviders(
    context: ActionContext<IFlightSearch, StoreState>,
    payload: {
      id: string;
      isFinancialProfile: boolean;
    },
  ) {
    try {
      const flightService = new FlightService();
      const response: ICoreResponse =
        await flightService.getActiveAirlinesAndAirlines(
          payload.id,
          payload.isFinancialProfile,
        );

      if (response.success && response.status === StatusCodes.OK) {
        const responseData: ACTIVE_PROVIDERS_AND_AIRLINES_API_RESPONSE =
          response.data;

        context.commit("addActiveAirlinesAndProviders", responseData);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
  },

  async fetchSelectedPassengerProfiles(
    context: ActionContext<IFlightSearch, StoreState>,
    {
      organizationId,
      passengerIds,
    }: { organizationId: string; passengerIds: string[] },
  ) {
    const missingPassengerIds = passengerIds.filter(
      (id) =>
        Boolean(id) &&
        !context.state.selectedPassengerProfiles.some(
          (passenger) => passenger.public_id === id,
        ),
    );

    if (!organizationId || missingPassengerIds.length === 0) return;

    try {
      context.commit("setFetchingSelectedPassengerProfiles", true);
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationPassengers(organizationId, {
          passengerIds: missingPassengerIds,
        });

      if (response.success && response.status === StatusCodes.OK) {
        const passengers: IPassenger[] = response.data || [];
        context.commit("upsertSelectedPassengerProfiles", passengers);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingSelectedPassengerProfiles", false);
    }
  },
};
export default actions;
